import React, { useState, useRef, useEffect } from 'react';
import { normalizePhoneNumber } from '../utils/phone';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const ForgotPasswordFlow: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState(['', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [timer, setTimer] = useState(0);
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [slideDir, setSlideDir] = useState<'in' | 'out'>('in');
    const [visible, setVisible] = useState(true);
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    useEffect(() => {
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    useEffect(() => {
        if (step === 2) {
            setTimeout(() => otpRefs.current[0]?.focus(), 400);
            if ('OTPCredential' in window) {
                const ac = new AbortController();
                (navigator.credentials.get as any)({ otp: { transport: ['sms'] }, signal: ac.signal })
                    .then((cred: any) => {
                        if (cred?.code) {
                            const digits = cred.code.replace(/\D/g, '').slice(0, 4).split('');
                            const next = ['', '', '', ''];
                            digits.forEach((d: string, i: number) => { next[i] = d; });
                            setOtp(next);
                        }
                    })
                    .catch(() => { });
                return () => ac.abort();
            }
        }
    }, [step]);

    const startTimer = (seconds = 120) => {
        setTimer(seconds);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) { if (timerRef.current) clearInterval(timerRef.current); return 0; }
                return prev - 1;
            });
        }, 1000);
    };

    const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    const goToStep = (next: 1 | 2 | 3 | 4) => {
        setVisible(false);
        setSlideDir('out');
        setTimeout(() => {
            setStep(next);
            setError(null);
            setSlideDir('in');
            setVisible(true);
        }, 280);
    };

    const handleOtpChange = (index: number, value: string) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const next = [...otp];
        next[index] = digit;
        setOtp(next);
        if (digit && index < 3) {
            otpRefs.current[index + 1]?.focus();
        }
        if (digit && index === 3) {
            const allFilled = next.every(d => d);
            if (allFilled) {
                setTimeout(() => handleVerifyOtp(next), 120);
            }
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4).split('');
        const next = ['', '', '', ''];
        digits.forEach((d, i) => { next[i] = d; });
        setOtp(next);
        const lastIdx = Math.min(digits.length, 3);
        otpRefs.current[lastIdx]?.focus();
        if (digits.length === 4) setTimeout(() => handleVerifyOtp(next), 120);
    };

    const handleSendOtp = async () => {
        const normalizedPhone = normalizePhoneNumber(phone);
        if (!normalizedPhone) { setError('شماره موبایل نامعتبر است'); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/auth/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: normalizedPhone, purpose: 'reset_password' }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.status === 429) throw new Error('لطفاً ۶۰ ثانیه دیگر تلاش کنید');
            if (!res.ok) throw new Error(data?.detail || data?.message || 'خطا در ارسال کد');
            startTimer(120);
            goToStep(2);
        } catch (err: any) {
            setError(err.message || 'خطای ناشناخته');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (otpValues?: string[]) => {
        const otpStr = (otpValues || otp).join('');
        const normalizedPhone = normalizePhoneNumber(phone);
        if (!normalizedPhone || otpStr.length !== 4) { setError('کد ۴ رقمی را کامل وارد کنید'); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/auth/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: normalizedPhone, otp: otpStr, purpose: 'reset_password' }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.status === 400 || res.status === 404) throw new Error('کد نامعتبر یا منقضی شده است');
            if (res.status === 429) throw new Error('لطفاً ۶۰ ثانیه دیگر تلاش کنید');
            if (!res.ok) throw new Error(data?.detail || data?.message || 'تایید کد ناموفق بود');
            goToStep(3);
        } catch (err: any) {
            setError(err.message || 'خطا در تایید کد');
            setOtp(['', '', '', '']);
            setTimeout(() => otpRefs.current[0]?.focus(), 50);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (newPassword.length < 8) { setError('رمز عبور باید حداقل ۸ کاراکتر باشد'); return; }
        if (newPassword !== confirmPassword) { setError('رمز عبور و تکرار آن یکسان نیستند'); return; }
        const normalizedPhone = normalizePhoneNumber(phone);
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: normalizedPhone, otp: otp.join(''), new_password: newPassword, purpose: 'reset_password' }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.status === 400 || res.status === 404) throw new Error('کد تایید نامعتبر یا منقضی شده');
            if (!res.ok) throw new Error(data?.detail || data?.message || 'تغییر رمز ناموفق بود');
            localStorage.setItem('token', data.token);
            if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
            goToStep(4);
        } catch (err: any) {
            setError(err.message || 'خطا در تغییر رمز');
        } finally {
            setLoading(false);
        }
    };

    const passwordStrength = (pass: string) => {
        if (!pass) return 0;
        let s = 0;
        if (pass.length >= 8) s++;
        if (pass.length >= 12) s++;
        if (/[A-Z]/.test(pass)) s++;
        if (/[0-9]/.test(pass)) s++;
        if (/[^A-Za-z0-9]/.test(pass)) s++;
        return s;
    };

    const strength = passwordStrength(newPassword);
    const strengthLabel = ['', 'خیلی ضعیف', 'ضعیف', 'متوسط', 'قوی', 'خیلی قوی'][strength];
    const strengthColors = ['', '#e24b4a', '#ba7517', '#eab308', '#22c55e', '#10b981'];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;500;600&display=swap');

                .fp-page {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                    font-family: 'Vazirmatn', sans-serif;
                    direction: rtl;
                    background-color: hsl(var(--background));
                }

                .fp-card {
                    width: 100%;
                    max-width: 400px;
                    background: hsl(var(--card));
                    border: 1px solid hsl(var(--border));
                    border-radius: 16px;
                    padding: 2rem 1.75rem;
                    transition: opacity 0.28s ease, transform 0.28s cubic-bezier(.4,0,.2,1);
                }

                .fp-card.fp-exit {
                    opacity: 0;
                    transform: translateY(8px);
                }

                .fp-card.fp-enter {
                    opacity: 1;
                    transform: translateY(0);
                    animation: fpSlideUp 0.3s cubic-bezier(.2,.8,.3,1) both;
                }

                @keyframes fpSlideUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                .fp-progress {
                    display: flex;
                    gap: 5px;
                    margin-bottom: 1.75rem;
                }

                .fp-prog-seg {
                    height: 2px;
                    border-radius: 1px;
                    background: hsl(var(--border));
                    transition: width 0.4s cubic-bezier(.4,0,.2,1), background 0.4s ease;
                }

                .fp-prog-seg.active {
                    background: hsl(var(--foreground));
                }

                .fp-prog-seg.done {
                    background: hsl(var(--foreground) / 0.35);
                }

                .fp-back {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    background: none;
                    border: none;
                    color: hsl(var(--muted-foreground));
                    font-family: 'Vazirmatn', sans-serif;
                    font-size: 12px;
                    cursor: pointer;
                    padding: 0;
                    margin-bottom: 1.5rem;
                    transition: color 0.15s;
                }
                .fp-back:hover { color: hsl(var(--foreground)); }
                .fp-back svg { width: 13px; height: 13px; }

                .fp-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: hsl(var(--foreground));
                    text-align: center;
                    margin-bottom: 4px;
                }

                .fp-desc {
                    font-size: 12px;
                    color: hsl(var(--muted-foreground));
                    text-align: center;
                    line-height: 1.7;
                    margin-bottom: 1.75rem;
                }

                .fp-field { margin-bottom: 1rem; }

                .fp-label {
                    display: block;
                    font-size: 12px;
                    font-weight: 500;
                    color: hsl(var(--muted-foreground));
                    margin-bottom: 5px;
                }

                .fp-input {
                    width: 100%;
                    height: 42px;
                    background: hsl(var(--muted) / 0.4);
                    border: 1px solid hsl(var(--border));
                    border-radius: 10px;
                    padding: 0 12px;
                    color: hsl(var(--foreground));
                    font-family: 'Vazirmatn', sans-serif;
                    font-size: 14px;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    direction: ltr;
                }
                .fp-input::placeholder { color: hsl(var(--muted-foreground) / 0.5); }
                .fp-input:focus {
                    border-color: hsl(var(--foreground) / 0.5);
                    box-shadow: 0 0 0 3px hsl(var(--foreground) / 0.07);
                }
                .fp-input.fp-pw { padding-left: 40px; }

                .fp-pw-wrap { position: relative; }
                .fp-pw-eye {
                    position: absolute;
                    left: 0; top: 0; bottom: 0;
                    width: 40px;
                    display: flex; align-items: center; justify-content: center;
                    background: none; border: none; cursor: pointer;
                    color: hsl(var(--muted-foreground));
                    transition: color 0.15s;
                    padding: 0;
                }
                .fp-pw-eye:hover { color: hsl(var(--foreground)); }
                .fp-pw-eye svg { width: 15px; height: 15px; }

                .fp-error {
                    background: hsl(var(--destructive) / 0.08);
                    border: 1px solid hsl(var(--destructive) / 0.25);
                    border-radius: 8px;
                    padding: 9px 12px;
                    color: hsl(var(--destructive));
                    font-size: 12px;
                    margin-bottom: 1rem;
                    animation: fpShake 0.35s ease;
                }

                @keyframes fpShake {
                    0%,100% { transform: translateX(0); }
                    25%     { transform: translateX(-5px); }
                    75%     { transform: translateX(5px); }
                }

                .fp-btn {
                    width: 100%;
                    height: 42px;
                    border-radius: 10px;
                    border: none;
                    background: hsl(var(--foreground));
                    color: hsl(var(--background));
                    font-family: 'Vazirmatn', sans-serif;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    margin-top: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    transition: opacity 0.15s, transform 0.1s;
                }
                .fp-btn:hover:not(:disabled) { opacity: 0.88; }
                .fp-btn:active:not(:disabled) { transform: scale(0.985); }
                .fp-btn:disabled { opacity: 0.35; cursor: not-allowed; }

                .fp-btn-ghost {
                    width: 100%;
                    height: 40px;
                    border-radius: 10px;
                    border: 1px solid hsl(var(--border));
                    background: transparent;
                    color: hsl(var(--muted-foreground));
                    font-family: 'Vazirmatn', sans-serif;
                    font-size: 12px;
                    cursor: pointer;
                    margin-top: 10px;
                    transition: border-color 0.15s, color 0.15s;
                }
                .fp-btn-ghost:hover { border-color: hsl(var(--border) / 0.8); color: hsl(var(--foreground)); }

                .fp-divider {
                    display: flex; align-items: center; gap: 10px;
                    margin: 14px 0;
                }
                .fp-div-line { flex: 1; height: 1px; background: hsl(var(--border)); }
                .fp-div-txt { font-size: 11px; color: hsl(var(--muted-foreground)); }

                .fp-spinner {
                    width: 14px; height: 14px;
                    border: 1.5px solid hsl(var(--background) / 0.3);
                    border-top-color: hsl(var(--background));
                    border-radius: 50%;
                    animation: fpSpin 0.65s linear infinite;
                    flex-shrink: 0;
                }
                @keyframes fpSpin { to { transform: rotate(360deg); } }

                .fp-otp-row {
                    display: flex;
                    gap: 10px;
                    justify-content: center;
                    direction: ltr;
                    margin-bottom: 1rem;
                }

                .fp-otp-box {
                    width: 58px;
                    height: 62px;
                    background: hsl(var(--muted) / 0.4);
                    border: 1px solid hsl(var(--border));
                    border-radius: 12px;
                    text-align: center;
                    font-size: 1.4rem;
                    font-weight: 600;
                    color: hsl(var(--foreground));
                    font-family: 'Vazirmatn', sans-serif;
                    outline: none;
                    caret-color: hsl(var(--foreground));
                    transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
                }
                .fp-otp-box:focus {
                    border-color: hsl(var(--foreground) / 0.5);
                    box-shadow: 0 0 0 3px hsl(var(--foreground) / 0.07);
                    transform: scale(1.04);
                }
                .fp-otp-box.fp-otp-filled {
                    border-color: hsl(var(--foreground) / 0.4);
                    animation: fpOtpPop 0.18s cubic-bezier(.34,1.56,.64,1) both;
                }
                .fp-otp-box.fp-otp-error {
                    border-color: hsl(var(--destructive) / 0.6);
                    animation: fpShake 0.35s ease;
                }

                @keyframes fpOtpPop {
                    from { transform: scale(0.88); }
                    to   { transform: scale(1); }
                }

                .fp-otp-loading {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    height: 24px;
                    margin-bottom: 1rem;
                }
                .fp-otp-dot {
                    width: 5px; height: 5px;
                    border-radius: 50%;
                    background: hsl(var(--foreground) / 0.3);
                    animation: fpDotPulse 1.2s ease-in-out infinite;
                }
                .fp-otp-dot:nth-child(2) { animation-delay: 0.2s; }
                .fp-otp-dot:nth-child(3) { animation-delay: 0.4s; }
                @keyframes fpDotPulse {
                    0%,80%,100% { transform: scale(1); opacity: 0.3; }
                    40%         { transform: scale(1.4); opacity: 1; }
                }

                .fp-timer {
                    text-align: center;
                    font-size: 11px;
                    color: hsl(var(--muted-foreground));
                    margin-bottom: 1rem;
                    height: 18px;
                }
                .fp-timer-num {
                    font-weight: 600;
                    color: hsl(var(--foreground));
                    font-variant-numeric: tabular-nums;
                }
                .fp-resend {
                    background: none; border: none;
                    color: hsl(var(--foreground));
                    font-family: 'Vazirmatn', sans-serif;
                    font-size: 11px;
                    cursor: pointer;
                    text-decoration: underline;
                    text-underline-offset: 3px;
                    padding: 0;
                }

                .fp-str-bar {
                    display: flex; gap: 3px;
                    margin-top: 6px;
                }
                .fp-str-seg {
                    height: 2px; flex: 1;
                    border-radius: 1px;
                    background: hsl(var(--border));
                    transition: background 0.25s;
                }
                .fp-str-lbl {
                    font-size: 10px;
                    margin-top: 4px;
                    text-align: right;
                    color: hsl(var(--muted-foreground));
                    transition: color 0.25s;
                }

                .fp-match-hint {
                    font-size: 10px;
                    margin-top: 4px;
                    text-align: right;
                    transition: color 0.2s;
                }

                .fp-success {
                    text-align: center;
                    padding: .5rem 0;
                }
                .fp-success-ring {
                    width: 56px; height: 56px;
                    border-radius: 50%;
                    border: 1px solid hsl(var(--border));
                    background: hsl(var(--muted) / 0.5);
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1.25rem;
                    animation: fpSuccessIn 0.45s cubic-bezier(.34,1.56,.64,1) both;
                }
                @keyframes fpSuccessIn {
                    from { transform: scale(0.5); opacity: 0; }
                    to   { transform: scale(1); opacity: 1; }
                }
                .fp-success-ring svg { width: 22px; height: 22px; color: hsl(var(--foreground)); }

                @media (max-width: 480px) {
                    .fp-card { padding: 1.5rem 1.25rem; border-radius: 14px; }
                    .fp-otp-box { width: 52px; height: 56px; font-size: 1.25rem; }
                }
            `}</style>

            <div className="fp-page" dir="rtl">
                <div className={`fp-card ${visible ? 'fp-enter' : 'fp-exit'}`}>

                    {step < 4 && (
                        <>
                            {step > 1 && (
                                <button className="fp-back" onClick={() => goToStep((step - 1) as 1 | 2 | 3)}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                    بازگشت
                                </button>
                            )}

                            <div className="fp-progress">
                                {[1, 2, 3].map(s => (
                                    <div
                                        key={s}
                                        className={`fp-prog-seg ${step === s ? 'active' : step > s ? 'done' : ''}`}
                                        style={{ width: step === s ? '40%' : '30%' }}
                                    />
                                ))}
                            </div>

                            <div className="fp-title">
                                {step === 1 && 'فراموشی رمز عبور'}
                                {step === 2 && 'کد تایید'}
                                {step === 3 && 'رمز عبور جدید'}
                            </div>
                            <div className="fp-desc">
                                {step === 1 && 'شماره موبایل خود را وارد کنید'}
                                {step === 2 && <>کد ۴ رقمی ارسال‌شده به <span style={{ fontVariantNumeric: 'tabular-nums', direction: 'ltr', display: 'inline-block' }}>{phone}</span> را وارد کنید</>}
                                {step === 3 && 'رمز عبور جدید خود را انتخاب کنید'}
                            </div>
                        </>
                    )}

                    {error && <div className="fp-error">{error}</div>}

                    {step === 1 && (
                        <>
                            <div className="fp-field">
                                <label className="fp-label">شماره موبایل</label>
                                <input
                                    className="fp-input"
                                    type="tel"
                                    inputMode="numeric"
                                    placeholder="09123456789"
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && !loading && phone && handleSendOtp()}
                                    autoComplete="tel"
                                    dir="ltr"
                                />
                            </div>
                            <button className="fp-btn" onClick={handleSendOtp} disabled={loading || !phone}>
                                {loading ? <><div className="fp-spinner" />در حال ارسال</> : 'ارسال کد تایید'}
                            </button>
                            <div className="fp-divider">
                                <div className="fp-div-line" />
                                <span className="fp-div-txt">یا</span>
                                <div className="fp-div-line" />
                            </div>
                            <button className="fp-btn-ghost" onClick={() => navigate('/login')}>
                                بازگشت به صفحه ورود
                            </button>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            {loading ? (
                                <div className="fp-otp-loading">
                                    <div className="fp-otp-dot" />
                                    <div className="fp-otp-dot" />
                                    <div className="fp-otp-dot" />
                                </div>
                            ) : null}
                            <div className="fp-otp-row" onPaste={handleOtpPaste}>
                                {otp.map((digit, i) => (
                                    <input
                                        key={i}
                                        ref={el => { otpRefs.current[i] = el; }}
                                        className={`fp-otp-box${digit ? ' fp-otp-filled' : ''}${error ? ' fp-otp-error' : ''}`}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={e => handleOtpChange(i, e.target.value)}
                                        onKeyDown={e => handleOtpKeyDown(i, e)}
                                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                                        disabled={loading}
                                    />
                                ))}
                            </div>

                            <div className="fp-timer">
                                {timer > 0 ? (
                                    <>ارسال مجدد تا <span className="fp-timer-num">{formatTime(timer)}</span></>
                                ) : (
                                    <button className="fp-resend" onClick={handleSendOtp} disabled={loading}>
                                        ارسال مجدد کد
                                    </button>
                                )}
                            </div>

                            <button
                                className="fp-btn"
                                onClick={() => handleVerifyOtp()}
                                disabled={loading || otp.some(d => !d)}
                            >
                                {loading ? <><div className="fp-spinner" />در حال تایید</> : 'تایید کد'}
                            </button>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <div className="fp-field">
                                <label className="fp-label">رمز عبور جدید</label>
                                <div className="fp-pw-wrap">
                                    <input
                                        className="fp-input fp-pw"
                                        type={showNewPass ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                        autoComplete="new-password"
                                        dir="ltr"
                                    />
                                    <button className="fp-pw-eye" type="button" onClick={() => setShowNewPass(p => !p)}>
                                        {showNewPass ? (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                                                <line x1="1" y1="1" x2="23" y2="23" />
                                            </svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {newPassword && (
                                    <>
                                        <div className="fp-str-bar">
                                            {[1, 2, 3, 4, 5].map(s => (
                                                <div
                                                    key={s}
                                                    className="fp-str-seg"
                                                    style={{ background: s <= strength ? strengthColors[strength] : undefined }}
                                                />
                                            ))}
                                        </div>
                                        <div className="fp-str-lbl" style={{ color: strengthColors[strength] }}>
                                            {strengthLabel}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="fp-field">
                                <label className="fp-label">تکرار رمز عبور</label>
                                <div className="fp-pw-wrap">
                                    <input
                                        className="fp-input fp-pw"
                                        type={showConfirmPass ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        autoComplete="new-password"
                                        onKeyDown={e => e.key === 'Enter' && !loading && handleResetPassword()}
                                        style={
                                            confirmPassword
                                                ? { borderColor: confirmPassword === newPassword ? 'hsl(var(--border))' : 'hsl(var(--destructive) / 0.5)' }
                                                : {}
                                        }
                                        dir="ltr"
                                    />
                                    <button className="fp-pw-eye" type="button" onClick={() => setShowConfirmPass(p => !p)}>
                                        {showConfirmPass ? (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                                                <line x1="1" y1="1" x2="23" y2="23" />
                                            </svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {confirmPassword && confirmPassword !== newPassword && (
                                    <div className="fp-match-hint" style={{ color: 'hsl(var(--destructive))' }}>
                                        رمزها یکسان نیستند
                                    </div>
                                )}
                            </div>

                            <button
                                className="fp-btn"
                                onClick={handleResetPassword}
                                disabled={loading || !newPassword || newPassword !== confirmPassword}
                            >
                                {loading ? <><div className="fp-spinner" />در حال ذخیره</> : 'تغییر رمز عبور'}
                            </button>
                        </>
                    )}

                    {step === 4 && (
                        <div className="fp-success">
                            <div className="fp-success-ring">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                            <div className="fp-title" style={{ marginBottom: '6px' }}>رمز تغییر کرد</div>
                            <div className="fp-desc">رمز عبور شما با موفقیت به‌روز شد</div>
                            <button className="fp-btn" onClick={() => navigate('/login')}>
                                ورود به حساب
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ForgotPasswordFlow;
